// Modal "Función exclusiva" para la generación de video — mismo diseño que
// el de los flujos de video (ConfigurarVideoInfluencerDetalle /
// ConfigurarVideoPersonalizado): video de muestra + texto "disponible
// únicamente para los planes Pro y Ultra" + CTA a planes.
//
// Extraído a componente compartido para reusarlo (p. ej. en Interfaz V2 al
// intentar generar video con un plan que no lo permite).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Lock, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { fetchStripeBillingPortalUrl } from '../lib/paymentServer';
import { gestionaPlanEnPortalStripe } from '../lib/gestionPlan';

export interface VideoPlanRestrictionModalProps {
  open: boolean;
  onClose: () => void;
  /** Plan actual del usuario (para la copy del CTA). */
  userPlan?: string | null;
}

export const VideoPlanRestrictionModal: React.FC<
  VideoPlanRestrictionModalProps
> = ({ open, onClose, userPlan }) => {
  const navigate = useNavigate();
  // Cliente de Stripe con suscripción activa → su cambio de plan va al Billing
  // Portal, NO a nuestro catálogo (ahí el checkout le crearía una SEGUNDA
  // suscripción). Arranca en `false` = catálogo, que es el destino seguro
  // mientras se resuelve. Ver lib/gestionPlan.ts y ADR-661.
  const [enPortalStripe, setEnPortalStripe] = useState(false);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) return;
        const { data: company } = await supabase
          .from('company_profiles')
          .select('plataforma_pago, plan')
          .eq('id_company', profile.id_company)
          .single();
        setEnPortalStripe(
          gestionaPlanEnPortalStripe(
            (company as any)?.plataforma_pago,
            (company as any)?.plan,
          ),
        );
      } catch {
        /* noop: queda en `false` → catálogo */
      }
    })();
  }, [open]);

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningBillingPortal(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      window.location.href = await fetchStripeBillingPortalUrl(user.id);
    } catch (e: any) {
      alert(e?.message || 'Error al abrir el portal de facturación');
      setOpeningBillingPortal(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center sm:justify-center sm:p-4">
      <div className="bg-surface-card w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg shadow-md relative flex flex-col sm:flex-row sm:overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-sm text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 inline-flex items-center justify-center transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="sm:hidden text-[17px] font-semibold text-brand-black/90 text-center pt-16 pb-4 px-5">
          Función exclusiva
        </h3>
        <div className="relative flex-shrink-0 px-5 sm:px-0 sm:w-1/2">
          <video
            src="/images/video_animar_imagen.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full max-w-[280px] mx-auto sm:max-w-none aspect-[4/5] object-cover rounded-md sm:rounded-none sm:rounded-l-lg"
          />
          <div className="absolute -bottom-3 right-8 w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center shadow-md sm:hidden">
            <Lock className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="flex-1 flex flex-col justify-center p-5 pt-8 pb-8 sm:p-6">
          <h3 className="hidden sm:block text-[17px] font-semibold text-brand-black/90 text-center mb-3">
            Función exclusiva
          </h3>
          <p className="text-[15px] text-neutral-500 mb-5 sm:text-center">
            La generación de videos está disponible únicamente para los usuarios
            de los planes{' '}
            <span className="font-semibold text-brand-black/80">Pro</span> y{' '}
            <span className="font-semibold text-brand-black/80">Ultra</span>.
          </p>
          <div className="flex flex-col gap-2 mt-auto pb-4 sm:mt-0 sm:pb-0">
            {!enPortalStripe ? (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/subscription');
                }}
                className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
              >
                {userPlan === 'gratis' ? 'Ver planes' : 'Mejorar plan'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleOpenBillingPortal}
                disabled={openingBillingPortal}
                className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
              >
                {openingBillingPortal ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Mejorar plan'
                )}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center w-full h-10 bg-surface-card hover:bg-neutral-100 text-neutral-600 border border-neutral-200 rounded-md text-[15px] font-medium shadow-sm transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlanRestrictionModal;
